<template>
    <form class="col-md-8 mx-auto" @submit.prevent="onSubmit">
        <div class="loader" v-if="loading"></div>
        <div v-if="!loading">
            <div class="btn-group" role="group" aria-label="Basic example">
                <button type="button"
                        class="btn btn-primary"
                        @click="backToRegister">
                    <i class="fas fa-th-list"></i> {{ $t('backToList') }}
                </button>
            </div>
            <page-title :title="$t('project.title')" :showTitle="false"></page-title>
            <div class="card row">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('project.information') }}</div>
                </div>
                <div class="card-body">
                    <div class="row float-bott">
                        <div class="form-group col-4">
                            <label :for="'number' + _uid" class="font-weight-bold required">{{ $t('project.number')
                                }}</label>
                            <input type="number"
                                   class="form-control"
                                   :id="'number' + _uid"
                                   :name="'number' + _uid"
                                   v-model="project.number"
                                   :data-vv-as="$t('project.number')"
                                   readonly/>
                        </div>
                        <div class="form-group col-4">
                            <label :for="'contract' + _uid" class="font-weight-bold required">{{ $t('project.contract')
                                }}</label>
                            <input type="text"
                                   class="form-control"
                                   :id="'contract' + _uid"
                                   :name="'contract' + _uid"
                                   v-model="project.contract"
                                   v-validate="'required'"
                                   :data-vv-as="$t('project.contract')"/>
                            <span class="text-danger" v-if="errors.has('contract' + _uid)">{{ errors.first('contract' + _uid) }}</span>

                        </div>
                        <div class="form-group col-4">
                            <label :for="'funding' + _uid" class="font-weight-bold required">{{ $t('project.funding')
                                }}</label>
                            <dropdown :load-by-parent-sort="true"
                                      :parent-sort="nomenclature.FundingType"
                                      v-model="project.fundingTypeId"
                                      :required="true"
                                      :fieldName="$t('project.funding')"></dropdown>
                            <span class="text-danger" v-if="errors.has('fundingType' + _uid)">{{ errors.first('fundingType' + _uid) }}</span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="form-group col-12">
                            <label :for="'name' + _uid" class="font-weight-bold required">{{ $t('project.name')
                                }}</label>
                            <textarea type="text"
                                      class="form-control"
                                      :id="'name' + _uid"
                                      :name="'name' + _uid"
                                      v-model="project.name"
                                      v-validate="'required'"
                                      :data-vv-as="$t('project.name')"/>
                            <span class="text-danger"
                                  v-if="errors.has('name' + _uid)">{{ errors.first('name' + _uid) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('project.partners') }}</div>
                </div>
                <div v-for="(organizer,index) in project.organizers" :key="index">
                    <div class="card-body k-display-flex k-align-items-center">
                        <div class="card-body col-4">
                            <label :for="'name' + _uid" class="font-weight-bold">{{ $t('project.name') }}</label>
                            <dynamicDropdown :apiUrl="'/api/organizers/'"
                                             v-model="organizer.organizerId"
                                             :required="true"
                                             :fieldName="$t('project.partners')"></dynamicDropdown>
                        </div>
                        <div class="card-body col-8">
                            <label :for="'name' + _uid" class="font-weight-bold">{{ $t('project.partnerContract')
                                }}</label>
                            <div v-if="organizer.organizerId">
                                <dynamicDropdown
                                    :apiUrl="`/api/organizers/getOrganizerContracts/${organizer.organizerId}`"
                                    v-model="organizer.organizerContractId"
                                    :fieldName="$t('project.partnerContract')"
                                    :registerName="$t('registers.organizers.title')"></dynamicDropdown>
                            </div>
                        </div>
                        <div>
                            <i class="fas fa-trash-alt btn-danger pointer" @click="deleteOrganizer(index)"></i>
                        </div>
                    </div>
                </div>
                <div class="mt-2 ml-2 mb-1">
                    <button type="button" class="btn btn-primary btn-md mr-2" @click="addPartner"><i
                        class="fas fa-plus mr-2"></i>{{ $t('project.addPartner') }}
                    </button>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('project.activities') }}</div>
                </div>
                <div v-for="(activity,index) in project.activities" :key="index">
                    <div class="card-body k-display-flex k-align-items-center">
                        <div class="col-12">
                            <dynamicDropdown :apiUrl="`/api/dropdown/GetActivities`"
                                             v-model="activity.activityId"
                                             :fieldName="$t('project.activity')"
                                             :registerName="$t('activity.title')"></dynamicDropdown>
                        </div>
                        <i class="fas fa-trash-alt  float-right btn-danger pointer"
                           @click="deleteActivity(index)"></i>
                    </div>
                </div>
                <div class="mt-2 ml-2 mb-1">
                    <button type="button" class="btn btn-primary btn-md mr-2" @click="addActivity"><i
                        class="fas fa-plus mr-2"></i>{{ $t('project.addActivity') }}
                    </button>
                </div>
            </div>

            <div class="card row mt-5">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('project.projectActivities') }}</div>
                </div>
                <div class="card-body">
                    <div v-for="(projectActivity,index) in project.projectActivities" :key="index"
                         class="k-display-flex k-align-items-center">
                        <input type="text"
                               class="form-control"
                               v-model="project.projectActivities[index]"/>

                        <i class="fas fa-trash-alt float-right btn-danger pointer"
                           @click="deleteProjectActivity(index)"></i>
                    </div>
                </div>
                <div class="ml-2 mb-1">
                    <button type="button" class="btn btn-primary btn-md mr-2" @click="addProjectActivity"><i
                        class="fas fa-plus mr-2"></i>{{ $t('project.projectActivity') }}
                    </button>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('otherFields.otherFields') }}</div>
                </div>
                <div class="card-body">
                    <otherFields :otherFields="project.otherFields"></otherFields>
                </div>
            </div>

            <AuxiliaryRegister ref="auxiliaryRegisterRef"
                               :register-id="AuxiliaryRegisters.Projects"
                               register-name="Projects"
                               :entry-id="project.auxRegisterEntityId"></AuxiliaryRegister>

            <div class="text-center mt-2 mb-5">
                <button type="button" class="btn btn-primary btn" @click="onSubmit">
                    <i class="fas fa-save"></i>
                    {{ $t('buttons.save.btnText') }}
                </button>
            </div>
        </div>
    </form>
</template>

<script>
    import Dropdown from '../../../components/dropdown/NomenclatureDropdown';
    import OtherFields from '../../../components/otherFields/OtherFields.vue';
    import DynamicDropdown from '../../../components/dropdown/DynamicDropdown';
    import PageTitle from '../../../components/common/PageTitle';
    import { Project, OrganizerContracts, Activity } from '@/models/models';
    import { AuxiliaryRegisters, NomenclatureParentBySort } from '@/models/enums';
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';

    export default {
        components: {
            Dropdown,
            PageTitle,
            OtherFields,
            DynamicDropdown,
            AuxiliaryRegister,
        },
        data() {
            return {
                nomenclature: NomenclatureParentBySort,
                id: null,
                project: new Project(),
                loading: false,
                isSaved: false,
                AuxiliaryRegisters: AuxiliaryRegisters
            }
        },
        methods: {
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.project.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();
                        if (this.id) {
                            this.edit();
                        } else {
                            this.create();
                        }
                    }
                })
            },
            addPartner(event) {
                this.project.organizers.push(new OrganizerContracts());
            },
            addActivity(event) {
                this.project.activities.push(new Activity());
            },
            addProjectActivity(event) {
                this.project.projectActivities.push('');
            },
            deleteActivity: function (m) {
                this.project.activities.splice(m, 1);
            },
            deleteOrganizer: function (m) {
                this.project.organizers.splice(m, 1);
            },
            deleteProjectActivity: function (m) {
                this.project.projectActivities.splice(m, 1);
            },
            backToRegister() {
                this.$router.push('/registers/projects');
            },
            create() {
                this.loading = true;
                this.$http.post('/api/projects', this.project).then(result => {
                    this.loading = false;
                    this.isSaved = true;
                    this.$vs.notify({
                        title: this.$t('notifications.success'),
                        text: this.$t('notifications.successfulSavedData'),
                        position: 'top-center',
                        color: 'success'
                    });
                    this.backToRegister();
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.error'),
                        text: this.$helpers.errorText(error),
                        position: 'top-center',
                        color: 'danger'
                    });
                });
            },
            edit() {
                this.loading = true;
                this.$http.put('/api/projects', this.project).then(result => {
                    this.loading = false;
                    this.isSaved = true;
                    this.$vs.notify({
                        title: this.$t('notifications.success'), // TODO:
                        text: this.$t('notifications.successfulSavedData'), // TODO:
                        position: 'top-center',
                        color: 'success'
                    });
                    this.backToRegister();
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.error'), // TODO:
                        text: this.$helpers.errorText(error), // TODO:
                        position: 'top-center',
                        color: 'danger'
                    });
                });
            },
            loadData() {
                this.loading = true;
                this.$http.get(`/api/projects/${this.id}`).then(result => {
                    this.project = new Project(result.data);
                    this.$store.initialModel = _.cloneDeep(this.project);
                    this.loading = false;
                }).catch(error => {
                    // TODO: Notify
                    this.loading = false;
                });
            }
        },
        beforeRouteLeave(to, from, next) {
            if (!_.isEqual(this.$store.initialModel, this.project) && !this.isSaved) {
                const answer = window.confirm(this.$t('notifications.leavePage'))
                if (answer) {
                    next()
                } else {
                    next(false)
                }
            } else {
                next()
            }
        },
        mounted() {
            this.id = this.$route.params.id;
            if (this.id) {
                this.loadData();
            }
            this.$store.initialModel = _.cloneDeep(this.project);
        }
    }
</script>

<style>
</style>
